<script setup lang="ts">
import { computed, ref, PropType, watch, onMounted } from "vue";
import {
    AssociatedDirectInput,
    Department,
    DirectLink,
    Participant,
    ProductLanguages,
    Project,
    Recipients,
    Trainer,
} from "../../types/PersologOnlineTypes.ts";
import { useI18n } from "vue-i18n";
import Button from "primevue/button";
import ProjectDetailsDirectLink from "./ProjectDetailsDirectLink.vue";
import ProjectDetailsMenu from "./ProjectDetailsMenu.vue";
import ProjectDetailsParticipants from "./ProjectDetailsParticipants.vue";
import DepartmentList from "./DepartmentList.vue";
import RolesList from "./RolesList.vue";
import {
    BENCHMARK_BASIC,
    BENCHMARK_JOBMATCH,
    ensureProductsWithDepartmentsLoaded,
    getProductQuantity,
    hasGroupChart,
    productHasBenchmarkDiagram,
    productHasDepartments,
    productHasRoles,
    recalculateProfileForAllParticipants,
} from "../../utils/ProjectUtils";
import GroupChart from "./groupchart/GroupChart.vue";
import { isMobileView, copyToClipboard, removeQueryParameter, removeQueryParameters, focusFirstElementInDialog } from "../../utils/GeneralUtils.ts";
import { useRoute, useRouter } from "vue-router";
import EditableBenchmarkDiagram from "./job-match/EditableBenchmarkDiagram.vue";
import ProjectCallToAction from "./ProjectCallToAction.vue";
import ViewBenchmarkProfileButton from "./job-match/ViewBenchmarkProfileButton.vue";
import GenerateBenchmarkProfileButton from "./job-match/GenerateBenchmarkProfileButton.vue";
import { CalculatorAPI, DirectInputAPI, ProfileAdministrationAPI } from "../../api/index.ts";
import Skeleton from "primevue/skeleton";
import { makeAssociatedDirectInput } from "../../utils/JobMatchUtils.ts";
import useProfileView from "../../utils/buttons-logic/profileViewLogic.ts";
import { fetchGeneratedProfileLanguages } from "../../utils/PdfUtils.ts";
import ProfileViewConfirmationDialog from "../common/ProfileViewConfirmationDialog.vue";
import {
    computeConfirmationDialogKey,
    getGeneratedParticipants as selectGeneratedParticipants,
    getRangeFromDirectInput as rangeFromDirectInput,
    hasGeneratedParticipants,
    pickProfileLanguage,
    resolvePdfLinkType,
} from "../../utils/ProjectDetailsUtils.ts";
const { t } = useI18n();

const props = defineProps({
    product: {
        type: String,
        required: true,
    },
    project: {
        type: Object as PropType<Project>,
        required: true,
    },
    directLink: {
        type: Object as PropType<DirectLink | null>,
        default: null,
    },
    directInput: {
        type: Object as PropType<AssociatedDirectInput | null>,
        default: null,
    },
    participants: {
        type: Array as PropType<Participant[]>,
        default: () => [],
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
    languages: {
        type: Object as PropType<ProductLanguages | null>,
        default: null,
    },
    visible: {
        type: Boolean,
        default: false,
    },
});

// Router
const router = useRouter();
const route = useRoute();

// Controls behaviour of the profile view confirmation dialog
const confirmationDialogVisible = ref(false);
const pdfTypeToView = ref<"group-chart" | "profile">("profile");
// The group chart (by its chartId) that an email deep link asked to open, or null.
const groupChartIdToOpen = ref<number | null>(null);

// Key for the confirmation dialog: the chartId for group-chart links, otherwise the
// participantId (profile links).
const confirmationDialogKey = computed(() =>
    computeConfirmationDialogKey(route.query.chartId, route.query.participantId),
);

const isProjectVisible = ref(props.visible);
const projectLoading = ref(false);
const loadingProductQuantity = ref(false);

const updatedProject = ref<Project>(props.project);
const localParticipants = ref<Participant[]>(props.participants ?? []);
const departments = ref<Department[]>([]);
const productHasGroupChart = ref(false);
const hasRoles = ref(false);

const productAvailableQuantity = ref();

// JobMatch Profiles related variables
const diagramIsEditable = ref(false);
const disableBenchmarkEditButton = ref(false);
const rangeValues = ref();
const benchmarkProfileGenerated = ref(false);
const disableBenchmarkViewButton = ref(true);

let intervalID: ReturnType<typeof setInterval>;

const emit = defineEmits([
    "update:delete",
    "update:participants",
    "update:project",
    "close",
    "reload:participants",
    "update:departments",
    "open:benchmark",
]);

function handleDelete(id: number) {
    isProjectVisible.value = false;
    removeQueryParameter(route, router, "projectId");
    emit("update:delete", id);
}

function handleProjectUpdate(project: Project) {
    if (updatedProject.value.id === project.id) {
        updatedProject.value = project;
        updateProductQuantity();
    }
    emit("update:project", project);
}

/**
 * Emits project ID to assign departments to the correct project
 * @param departmentsList
 */
function handleDepartmentsUpdate(departmentsList: Department[]) {
    departments.value = departmentsList;
    emit("update:departments", props.project.id, departmentsList);
}

/**
 * Fetches the available quantity of a product.
 */
async function updateProductQuantity() {
    try {
        loadingProductQuantity.value = true;
        productAvailableQuantity.value = await getProductQuantity(props.trainer.id, props.project?.defaultProduct);
    } catch (error) {
        console.error("Error fetching product quantity:", error);
    } finally {
        loadingProductQuantity.value = false;
    }
}

/**
 * Closes the dialog and emits the close event.
 * @emits close
 */
function handleCloseDialog() {
    isProjectVisible.value = false;
    removeQueryParameter(route, router, "projectId");
    emit("close");
}

function copyLink() {
    copyToClipboard(`${globalThis.location.href}?projectId=${props.project.id}`);
}

function getRangeFromDirectInput(competencyName: string) {
    return rangeFromDirectInput(props.directInput, competencyName);
}

function updateDirectInput(values: [number[], number[], number[], number[]]) {
    rangeValues.value = values;
}

function updateRangeValues(updatedRangeValues: [number[], number[], number[], number[]]) {
    rangeValues.value = updatedRangeValues;
}

function importToBenchmark(groupChartId?: number) {
    handleCloseDialog();
    emit("open:benchmark", props.project?.id, groupChartId ?? props.project?.id);
}

/**
 * Edits the benchmark diagramm and saves the changes.
 * Saving the changes recalculates the profile for all participants.
 */
async function editBenchmarkDiagramm() {
    if (diagramIsEditable.value) {
        const directInput = await DirectInputAPI.saveDirectInputGroup(
            props.project.trainer ?? "",
            makeAssociatedDirectInput(
                props.project.id!,
                BENCHMARK_BASIC,
                rangeValues.value[0],
                rangeValues.value[1],
                rangeValues.value[2],
                rangeValues.value[3],
            ),
        );
        await CalculatorAPI.calculateAndPersistDirectLinkGroup(props.project.trainer ?? "", directInput);
        recalculateProfileForAllParticipants(props.project.id!);
    }
    diagramIsEditable.value = !diagramIsEditable.value;
}

async function checkGroupChartConditions(project: Project) {
    return (project &&
        props.participants &&
        (await hasGroupChart(project)) &&
        project.recipients !== Recipients.PARTICIPANT);
}

/**
 * Polls participant data
 */
const startPolling = () => {
    fetchParticipants();
    intervalID = setInterval(fetchParticipants, 15000); // 30 seconds
};

const stopPolling = () => {
    clearInterval(intervalID);
};

/**
 * Fetches participants, but only updates the participant in participantMap if it is different to what is already stored.
 */
async function fetchParticipants(optimisticParticipants?: Participant[]) {
    if (optimisticParticipants) {
        localParticipants.value = optimisticParticipants;
    }
    if (props.project) {
        return ProfileAdministrationAPI.getParticipants(props.project.id!)
            .then((participants) => {
                const fetched = JSON.stringify(participants);
                // An unchanged list keeps its identity, so the table does not rebuild every row on each poll (RDT-484).
                if (fetched !== JSON.stringify(localParticipants.value)) {
                    localParticipants.value = participants;
                }
                if (fetched !== JSON.stringify(props.participants)) {
                    emit("update:participants", participants, props.project.id);
                }
            })
            .catch((error) => {
                console.error("Error fetching participants:", error);
            })
            .finally(() => {
                updateProductQuantity();
            });
    }
}

async function loadProjectData() {
    if (props.project) {
        projectLoading.value = true;
        await ensureProductsWithDepartmentsLoaded();
        await fetchParticipants();
        productHasGroupChart.value = await checkGroupChartConditions(props.project);
        hasRoles.value = await productHasRoles(props.project.product);
        projectLoading.value = false;
        updateProductQuantity();
    }
}

function showConfirmationDialog(type: "group-chart" | "profile") {
    pdfTypeToView.value = type;
    confirmationDialogVisible.value = true;
}

function closeConfirmationDialog() {
    confirmationDialogVisible.value = false;
    removeQueryParameters(route, router, ["participantId", "chartId", "language"]);
}

async function viewProfileInNewTab() {
    const queryParticipantId = Number(route.query.participantId);
    if (pdfTypeToView.value === "profile") {
        // Ensure participants are loaded and
        // read from localParticipants (the component's own fetched copy) rather than the prop, which
        // can still be undefined/empty on first load.
        if (!localParticipants.value.some((participant) => participant.id === queryParticipantId)) {
            await fetchParticipants();
        }
        const participant = localParticipants.value.find((participant) => participant.id === queryParticipantId);
        if (!participant) {
            return;
        }
        const { viewPdf } = useProfileView(ref(participant), ref(props.project));
        const languagesArray = (await fetchGeneratedProfileLanguages(
            participant.selectedProduct,
            participant.id!,
        )) as string[];
        // Prefer the language passed in the email link (the language the profile was last generated in).
        // Fall back to the first available language for older links without a language parameter.
        const requestedLanguage = route.query.language?.toString();
        const language = pickProfileLanguage(languagesArray, requestedLanguage);
        // Returning from the viewer should reopen THIS project without re-firing the confirmation.
        // Profile email links carry no projectId (the project is found via participantId)
        await router.replace({
            query: { ...route.query, projectId: String(props.project.id), participantId: undefined, language: undefined },
        });
        await viewPdf(language);
    } else if (pdfTypeToView.value === "group-chart") {
        // New links carry the specific chartId (legacy links carry none → nothing to auto-open).
        // Capture it, then clear the deep-link params in a SINGLE navigation
        const queryChartId = route.query.chartId;
        const chartIdToOpen = queryChartId === undefined ? null : Number(queryChartId);
        removeQueryParameters(route, router, ["chartId", "participantId"]);
        groupChartIdToOpen.value = chartIdToOpen;
    }
}

/**
 * Checks if the project has generated participants.
 */
function projectHasGeneratedParticipants() {
    return hasGeneratedParticipants(props.participants);
}

function getGeneratedParticipants() {
    return selectGeneratedParticipants(props.participants);
}

watch(
    () => isProjectVisible.value,
    (newValue) => {
        if (newValue) {
            startPolling();
        } else {
            stopPolling();
        }
    },
    { immediate: true },
);

watch(
    () => props.project,
    (newValue) => {
        updatedProject.value = newValue;
    },
);

watch(
    () => props.participants,
    (newValue) => {
        localParticipants.value = newValue ?? [];
    },
);

watch(
    () => props.directInput,
    (newValue) => {
        if (newValue) {
            rangeValues.value = [
                getRangeFromDirectInput("D"),
                getRangeFromDirectInput("I"),
                getRangeFromDirectInput("S"),
                getRangeFromDirectInput("G"),
            ];
        }
    },
);

/**
 * 1. Bring tab focus (accessibility) to the dialog when it is opened
 * 2. Updates range values if direct input is available
 * 3. Load project data
 * 4. Search for participantId in query and open profile in new tab if found (e.g. redirected from email)
 * 5. Disable benchmark view button if project is not generated
 */
onMounted(async () => {

    focusFirstElementInDialog(`project-details-view_dialog_view-project-${props.project.id}`);

    if (props.directInput) {
        rangeValues.value = [
            getRangeFromDirectInput("D"),
            getRangeFromDirectInput("I"),
            getRangeFromDirectInput("S"),
            getRangeFromDirectInput("G"),
        ];
    }
    loadProjectData();

    // Open the relevant PDF in a new tab when arriving from an email link.
    // - New group-chart links carry a specific chartId.
    // - Legacy group-chart links set participantId to the projectId.
    // - Profile links carry a participantId that differs from the projectId.
    const pdfLinkType = resolvePdfLinkType(route.query.chartId, route.query.participantId, props.project.id);
    if (pdfLinkType) {
        showConfirmationDialog(pdfLinkType);
    }

    if (props.project.status === "GENERATED") {
        disableBenchmarkViewButton.value = false;
    } else {
        disableBenchmarkViewButton.value = true;
    }
});
</script>

<template>
    <ProfileViewConfirmationDialog
        v-if="confirmationDialogVisible"
        :id="`profile-view-confirmation-dialog-${confirmationDialogKey}`"
        :participant-id="confirmationDialogKey"
        :type="pdfTypeToView"
        name="profile-view"
        @open:profile="viewProfileInNewTab"
        @close:dialog="closeConfirmationDialog"
    />
    <!--
        Holds 1550px while the window shrinks, so its share of the window grows from 80% to 97% instead of
        squeezing the participants table at a fixed 80% (RDT-456).
    -->
    <Dialog
        v-model:visible="isProjectVisible"
        :id="`project-details-view_dialog_view-project-${updatedProject.id}`"
        modal
        :style="{ width: 'clamp(80vw, 1550px, 97vw)', maxWidth: '1600px', maxHeight: '99vh' }"
        :breakpoints="{ '575px': '97vw' }"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
        @after-hide="handleCloseDialog"
    >
        <template #header>
            <div class="inline-flex items-center justify-center">
                <span class="font-light whitespace-nowrap mr-2">{{ updatedProject?.product?.toUpperCase() }}</span>
                <span v-if="!isMobileView()" class="font-bold whitespace-nowrap">{{ updatedProject?.name }}</span>
                <Button
                    text
                    rounded
                    size="small"
                    class="ml-2 link"
                    icon="pi pi-link"
                    @click="copyLink"
                    v-tooltip="{ value: t('project-details.participants.tooltips.copy'), showDelay: 500 }"
                />
            </div>
            <span
                v-if="updatedProject?.product !== BENCHMARK_JOBMATCH"
                :id="`project-details-view_quantity-chip-${updatedProject.id}`"
                class="quantity-chip ml-auto mr-4"
            >
                {{ t("common.profiles.availableProfiles") }}:
                <template v-if="!loadingProductQuantity">
                    {{ productAvailableQuantity ? productAvailableQuantity : 0 }}
                </template>
                <template v-else>
                    <i class="pi pi-spinner pi-spin"></i>
                </template>
            </span>
        </template>

        <!-- Skeleton -->
        <Transition>
            <div v-if="projectLoading" class="card">
                <div class="p-12">
                    <div class="flex mb-6">
                        <Skeleton shape="circle" size="4rem" class="mr-2"></Skeleton>
                        <div>
                            <Skeleton width="10rem" class="mb-2"></Skeleton>
                            <Skeleton width="5rem" class="mb-2"></Skeleton>
                            <Skeleton height=".5rem"></Skeleton>
                        </div>
                    </div>
                    <div class="flex justify-between mt-6">
                        <Skeleton width="4rem" height="1rem"></Skeleton>
                        <Skeleton width="4rem" height="1rem"></Skeleton>
                    </div>
                </div>
            </div>
        </Transition>

        <ProjectDetailsMenu
            v-if="isProjectVisible && !projectLoading"
            :trainer="trainer"
            :project="updatedProject"
            :participants="participants"
            :direct-link="directLink"
            :languages="languages"
            :departments="departments"
            :benchmark-range-values="rangeValues"
            :disable-benchmark-edit-button="disableBenchmarkEditButton"
            @update:delete-project="handleDelete"
            @update:project="handleProjectUpdate"
            @update:range-values="updateRangeValues"
            @update:benchmark-editable="diagramIsEditable = $event"
        />
        <div class="grid grid-cols-12 gap-4" v-if="!projectLoading">
            <div class="col-span-12 md:col-span-12">
                <DepartmentList
                    v-if="project && productHasDepartments(project.product)"
                    :project="project"
                    :participants="localParticipants"
                    @update:departments="handleDepartmentsUpdate"
                />
                <RolesList v-if="hasRoles" :project="project" :participants="participants" />
                <GroupChart
                    v-if="productHasGroupChart && project.product !== BENCHMARK_JOBMATCH"
                    :project="project"
                    :participants="participants"
                    :languages="languages"
                    :open-chart-id="groupChartIdToOpen"
                    @update-project="handleProjectUpdate"
                    @open:benchmark="importToBenchmark"
                    @tab:opened="groupChartIdToOpen = null"
                />
                <ProjectDetailsDirectLink
                    v-if="directLink"
                    :project="updatedProject"
                    :direct-link="directLink"
                    :trainer="trainer"
                    :participants="participants"
                    @update:project="handleProjectUpdate"
                    @reload:participants="fetchParticipants"
                />
            </div>

            <!-- Benchmark -->
            <div class="col-span-12 benchmark-diagram" v-if="productHasBenchmarkDiagram(props.project?.product)">
                <EditableBenchmarkDiagram
                    v-if="rangeValues"
                    :editable="diagramIsEditable"
                    :language="project.language"
                    :dominant-range="rangeValues[0]"
                    :initiative-range="rangeValues[1]"
                    :steady-range="rangeValues[2]"
                    :cautious-range="rangeValues[3]"
                    @update:range-values="updateDirectInput"
                />
                <div class="profile-buttons" style="display: flex; flex-wrap: wrap; justify-content: center; row-gap: 0.5rem; margin-top: 20px">
                    <GenerateBenchmarkProfileButton
                        :project="project"
                        :disabled="diagramIsEditable"
                        :has-generated-participant="projectHasGeneratedParticipants()"
                        :participants="getGeneratedParticipants()"
                        @profile:generated="
                            benchmarkProfileGenerated = true;
                            disableBenchmarkEditButton = false;
                            disableBenchmarkViewButton = false;
                        "
                        @profile:generation:started="
                            disableBenchmarkEditButton = true;
                            !benchmarkProfileGenerated;
                            disableBenchmarkViewButton = true;
                        "
                        @profile:generation:cancelled="disableBenchmarkEditButton = false"
                    />
                    <ViewBenchmarkProfileButton
                        :project="project"
                        :has-generated-participants="projectHasGeneratedParticipants()"
                        :disabled="diagramIsEditable || disableBenchmarkViewButton"
                    />
                    <!-- edit diagram -->
                    <Button
                        v-if="productHasBenchmarkDiagram(props.project?.product)"
                        style="margin-left: 10px"
                        :id="`project-details-menu_btn_edit-benchmark-diagram-${project?.id ?? 0}`"
                        :icon="diagramIsEditable ? 'pi pi-save' : 'pi pi-pencil'"
                        @click="editBenchmarkDiagramm"
                        :disabled="disableBenchmarkEditButton"
                        :label="
                            diagramIsEditable
                                ? t('buttons.saveChanges')
                                : t('project-configuration.jobMatch.editBenchmarkDiagram')
                        "
                    />
                </div>
            </div>
            <div v-if="isMobileView()" class="col-span-12 mb-0 pb-0">
                <h4>{{ t("header.accountSettings.accountLinks.actions") }}</h4>
            </div>
            <div class="mt-2 col-span-12 flex justify-between md:justify-start items-start">
                <ProjectCallToAction
                    :product-name="product"
                    :direct-link="directLink"
                    :participants="participants"
                    :project="project"
                    :trainer="trainer"
                    :product-available-quantity="productAvailableQuantity"
                    @update:participants="fetchParticipants"
                    @update:project="handleProjectUpdate"
                    @update:quantity="updateProductQuantity"
                />
            </div>
        </div>
        <ProjectDetailsParticipants
            v-if="!projectLoading && project.product !== BENCHMARK_BASIC"
            :product="product"
            :project="updatedProject"
            :participants="localParticipants"
            :trainer="trainer"
            :languages="languages"
            :can-add-participants="!directLink"
            :direct-link="directLink"
            @update:participants="(p) => fetchParticipants(p)"
            @reload:participants="fetchParticipants"
            @update:project="handleProjectUpdate"
        />
    </Dialog>
</template>

<style scoped>
.benchmark-diagram {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 1rem;
}

.quantity-chip {
    border-radius: 6px;
    border: 1px solid var(--p-secondary-500);
    padding: 0 12px;
    text-align: center;
    background: var(--p-secondary-500);
    font-size: var(--p-button-sm-font-size);
    font-weight: bold;
    color: white;
}

@media screen and (max-width: 768px) {
    .profile-buttons * {
        font-size: 0.8rem;
    }
}
</style>
